
'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function BeforeAfterSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section ref={ref} className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-6 sm:mb-10 md:mb-16"
        >
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black mb-2 sm:mb-4">
            Before &amp; After
          </h2>
          <p className="text-black/60 text-xs sm:text-sm md:text-base lg:text-lg max-w-2xl mx-auto">
            Your habit is your choice. Carrying it like a mess is not.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12">
          {/* Before */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative h-[280px] sm:h-[340px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-lg bg-gray-100">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-4 sm:p-6">
                  <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-2 sm:mb-4">🚬</div>
                  <h3 className="font-serif text-lg sm:text-xl md:text-2xl lg:text-3xl text-black mb-3 sm:mb-4">
                    Before
                  </h3>
                  <ul className="text-left space-y-2 sm:space-y-3 text-black/70">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✗</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Cheap disposable lighters that die
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✗</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Crushed cigarettes in your pocket
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✗</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Bent, torn, ugly packs
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✗</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Carrying it all separately
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* After */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative h-[280px] sm:h-[340px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-lg">
              <img
                src="https://static.readdy.ai/image/b66a7d3fce0ec8099d6508e6c0faefc9/218219c4ba2d7bd2fdaa59ea73ac98f3.png"
                alt="Premium Pistol Lighter"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end">
                <div className="p-4 sm:p-6 w-full">
                  <h3 className="font-serif text-lg sm:text-xl md:text-2xl lg:text-3xl text-white mb-2 sm:mb-4">
                    After
                  </h3>
                  <ul className="space-y-2 sm:space-y-3 text-white/90">
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✓</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Refillable lighter — never runs out
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✓</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Cigarettes stay perfect and protected
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✓</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">Premium look and feel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-0.5 text-xs sm:text-sm md:text-base flex-shrink-0">✓</span>
                      <span className="text-[11px] sm:text-xs md:text-sm lg:text-base">
                        Everything in one sleek device
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
